<script setup lang="ts">
import rtgLogo from "@/assets/images/RoomsToGoLogo.png";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import type {
  DashboardNavExternalLink,
  DashboardNavGroup,
  DashboardNavItem,
  DashboardNavLink,
  DashboardTheme,
  DashboardUserProfile,
} from "../../features/dashboard/dashboard.types.ts";
import { useHelpPanelStore } from "../../features/help/helpPanelStore.ts";
import { useRedistributionStore } from "../../stores/redistributionStore.ts";
import LucideIcon from "../icons/LucideIcon.vue";
import DashboardSidebarLink from "./DashboardSidebarLink.vue";
import DashboardSidebarNavItem from "./DashboardSidebarNavItem.vue";

const props = defineProps<{
  items: DashboardNavItem[];
  theme: DashboardTheme;
  user: DashboardUserProfile;
}>();

const emit = defineEmits<(event: "set-theme", value: DashboardTheme) => void>();

const route = useRoute();
const helpPanel = useHelpPanelStore();
const { redistributionResult } = storeToRefs(useRedistributionStore());

// Gate for any nav link marked `childrenGatedByResults` (currently PO
// Recommendations' Results/SKU Analysis/Inventory Health/Transfers sub-links).
const hasLatestResults = computed(
  () => redistributionResult.value !== undefined
);

const primaryItems = computed(() =>
  props.items.filter((item) => !item.pinToBottom)
);
const secondaryItems = computed(() =>
  props.items.filter((item) => item.pinToBottom)
);

const isCollapsed = ref(false);

const sidebarShellClass = computed(() =>
  isCollapsed.value
    ? "dashboard-sidebar-shell px-4 py-4 lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-[50px] lg:flex-col lg:overflow-hidden lg:border-r lg:border-b-0 lg:px-2 lg:py-4"
    : "dashboard-sidebar-shell px-4 py-4 lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-[17rem] lg:flex-col lg:overflow-hidden lg:border-r lg:border-b-0 lg:px-3 lg:py-6"
);

const brandRowClass = computed(() =>
  isCollapsed.value
    ? "flex items-center justify-center gap-3 lg:mb-6 lg:flex lg:w-full lg:justify-center"
    : "flex items-center justify-between gap-3 lg:mb-6 lg:flex lg:w-full lg:justify-center"
);

const collapseButtonLabel = computed(() =>
  isCollapsed.value ? "Expand sidebar" : "Collapse sidebar"
);

function toggleSidebar(): void {
  isCollapsed.value = !isCollapsed.value;
}

function isActive(item: DashboardNavLink | DashboardNavExternalLink): boolean {
  if (item.kind === "external") {
    return false;
  }

  const activeNavKey = route.meta.dashboardNavKey;
  if (typeof activeNavKey === "string") {
    return activeNavKey === item.routeName;
  }

  return String(route.name ?? "") === item.routeName;
}

/* ── Collapsible nav groups (and links-with-children) ──────────────────────── */

// Same expand/collapse mechanics apply to a `kind: "group"` toggle button and
// a `kind: "route"` link that also carries `children` (e.g. PO Recommendations).
type ExpandableNavItem = DashboardNavGroup | DashboardNavLink;

function visibleChildren(item: ExpandableNavItem): DashboardNavLink[] {
  const children = item.children ?? [];
  if (item.kind === "route" && item.childrenGatedByResults) {
    return hasLatestResults.value ? children : [];
  }
  return children;
}

function groupContainsActiveRoute(item: ExpandableNavItem): boolean {
  return (item.children ?? []).some(
    (child) => isActive(child) || groupContainsActiveRoute(child)
  );
}

// Recursively finds every group/link that can expand, at any nesting depth.
function expandableItems(items: DashboardNavItem[]): ExpandableNavItem[] {
  const found: ExpandableNavItem[] = [];
  for (const item of items) {
    if (item.kind === "group") {
      found.push(item, ...expandableItems(item.children));
    } else if (item.kind === "route" && item.children) {
      found.push(item, ...expandableItems(item.children));
    }
  }
  return found;
}

// Groups start collapsed, except the one holding the current route so a deep
// link never lands on a nav that hides where the user already is.
const expandedGroups = ref<Set<string>>(
  new Set(
    expandableItems(props.items)
      .filter((item) => groupContainsActiveRoute(item))
      .map((item) => item.key)
  )
);

watch(
  () => [route.name, route.meta.dashboardNavKey],
  () => {
    for (const item of expandableItems(props.items)) {
      if (groupContainsActiveRoute(item)) {
        expandedGroups.value = new Set(expandedGroups.value).add(item.key);
      }
    }
  }
);

function isGroupExpanded(item: { key: string }): boolean {
  return expandedGroups.value.has(item.key);
}

function toggleGroup(item: { key: string }): void {
  const next = new Set(expandedGroups.value);
  if (next.has(item.key)) {
    next.delete(item.key);
  } else {
    next.add(item.key);
  }
  expandedGroups.value = next;
}

// The 50px icon rail has no room for a group header plus indented children, and
// hiding pages behind a two-step interaction defeats the point of the rail, so
// groups/link-parents are flattened to their (visible) child icons while collapsed.
function flattenForRail(
  item: DashboardNavItem
): (DashboardNavLink | DashboardNavExternalLink)[] {
  if (item.kind === "group") {
    return item.children.flatMap((child) => flattenForRail(child));
  }
  if (item.kind === "route" && item.children) {
    return [
      item,
      ...visibleChildren(item).flatMap((child) => flattenForRail(child)),
    ];
  }
  return [item];
}

const collapsedRailItems = computed<
  (DashboardNavLink | DashboardNavExternalLink)[]
>(() => primaryItems.value.flatMap((item) => flattenForRail(item)));

/* ── Footer utility bar (theme + settings) ─────────────────────────────────── */

// Icon-only now, so the label has to survive somewhere accessible.
const nextTheme = computed<DashboardTheme>(() =>
  props.theme === "dark" ? "light" : "dark"
);

const themeToggleLabel = computed(() =>
  props.theme === "dark" ? "Switch to light theme" : "Switch to dark theme"
);

// Shows the mode you would switch TO, matching the action-oriented label.
const themeToggleIcon = computed(() =>
  props.theme === "dark" ? "sun" : "moon"
);

const settingsItem = computed(() =>
  secondaryItems.value.find((item) => item.kind === "route")
);
</script>

<template>
  <aside :class="sidebarShellClass">
    <div :class="brandRowClass">
      <RouterLink
        v-if="!isCollapsed"
        :to="{ name: 'dashboard-overview' }"
        class="inline-flex items-center lg:flex lg:w-full lg:justify-center"
      >
        <img class="h-10 w-auto" :src="rtgLogo" alt="Rooms To Go" />
      </RouterLink>
      <button
        type="button"
        class="dashboard-sidebar-collapse-toggle inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 transition-colors hover:bg-white/10 lg:shrink-0"
        :aria-label="collapseButtonLabel"
        :title="collapseButtonLabel"
        @click="toggleSidebar"
      >
        <span aria-hidden="true">{{ isCollapsed ? "❯" : "❮" }}</span>
      </button>
      <span
        v-if="!isCollapsed"
        class="dashboard-sidebar-pill px-3 py-1 text-[11px] font-semibold tracking-[0.2em] uppercase lg:hidden"
      >
        Insight.IQ
      </span>
    </div>

    <nav
      class="dashboard-nav mt-4 grid gap-2 lg:mt-0 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pe-1"
      aria-label="Dashboard navigation"
    >
      <template v-if="isCollapsed">
        <DashboardSidebarLink
          v-for="item in collapsedRailItems"
          :key="item.key"
          :item="item"
          :is-collapsed="true"
          :is-active="isActive(item)"
        />
      </template>

      <template v-else>
        <DashboardSidebarNavItem
          v-for="item in primaryItems"
          :key="item.key"
          :item="item"
          :is-active="isActive"
          :is-group-expanded="isGroupExpanded"
          :toggle-group="toggleGroup"
          :visible-children="visibleChildren"
        />
      </template>
    </nav>

    <div
      v-if="!isCollapsed"
      class="dashboard-sidebar-user-card mt-6 rounded-2xl px-4 py-4 lg:mt-6"
    >
      <div class="flex items-center gap-3">
        <span
          class="dashboard-sidebar-avatar inline-flex h-12 w-12 items-center justify-center rounded-full text-base font-semibold text-white"
        >
          {{ user.initials }}
        </span>
        <div class="min-w-0">
          <p class="dashboard-text-primary truncate text-sm font-semibold">
            {{ user.name }}
          </p>
          <p class="dashboard-text-secondary text-xs">{{ user.role }}</p>
        </div>
      </div>

      <div class="dashboard-divider mt-5 border-t pt-5">
        <p
          class="dashboard-text-primary text-[1.75rem] leading-none font-semibold"
        >
          Insight.IQ
        </p>
        <p class="dashboard-text-secondary mt-2 text-xs">by Rooms To Go</p>
      </div>
    </div>

    <!-- Icon-only utility bar. Theme and Settings live here rather than in the
         nav flow, which returns that vertical space to the nav list. Stacks in
         the collapsed rail, where neither control used to be reachable. -->
    <div
      class="dashboard-sidebar-utility-bar mt-4"
      :class="isCollapsed ? 'flex-col' : 'flex-row justify-center'"
    >
      <button
        type="button"
        class="dashboard-sidebar-utility-button"
        :class="{
          'dashboard-sidebar-utility-button--active': helpPanel.isOpen,
        }"
        aria-label="Help"
        title="Help"
        :aria-expanded="helpPanel.isOpen"
        @click="helpPanel.open()"
      >
        <LucideIcon name="circle-help" :size="20" />
      </button>

      <button
        type="button"
        class="dashboard-sidebar-utility-button"
        :aria-label="themeToggleLabel"
        :title="themeToggleLabel"
        @click="emit('set-theme', nextTheme)"
      >
        <LucideIcon :name="themeToggleIcon" :size="20" />
      </button>

      <RouterLink
        v-if="settingsItem"
        :to="{ name: settingsItem.routeName }"
        class="dashboard-sidebar-utility-button"
        :class="{
          'dashboard-sidebar-utility-button--active': isActive(settingsItem),
        }"
        :aria-label="settingsItem.label"
        :title="settingsItem.label"
      >
        <LucideIcon name="settings" :size="20" />
      </RouterLink>
    </div>
  </aside>
</template>
